import { CSSProperties, FC, ReactNode } from 'react';
import { format } from 'date-fns';
import classNames from 'classnames';
import { ColorField, ImageField } from '@prismicio/client';

import { Icon, PrismicImageWithAlt } from '@/components';
import { numberToShortString } from 'utils/utils';

import style from './blog.module.scss';

export const BlogFigureImage: FC<IFigureProps> = ({
  image,
  className,
  objectFit = 'cover',
  objectPosition = 'center top',
  showPlaceholder = true,
  defaultAlt = 'Blog post image',
  noImageContent,
}) => {
  if (!image?.url && !showPlaceholder) return null;

  return (
    <figure className={classNames(style.article__figure, className)}>
      <PrismicImageWithAlt
        defaultAlt={defaultAlt}
        field={image}
        fill
        noImageContent={noImageContent}
        showPlaceholderIfEmpty={showPlaceholder}
        style={{ objectFit, objectPosition }}
      />
    </figure>
  );
};

export const BlogPublishDate: FC<IBlogPublishDateProps> = ({
  publishAt,
  className,
}) => {
  if (!publishAt) return null;

  return (
    <time className={className} dateTime={publishAt.toISOString()}>
      {format(publishAt, 'PPP')}
    </time>
  );
};

const BlogCounter: FC<IBlogCounterProps> = ({
  value = 0,
  icon = 'visualizations',
  iconColor,
  countColor,
  className,
}) => {
  if (!value) return null;

  return (
    <span
      className={className}
      style={{
        color: countColor!,
      }}
    >
      <Icon
        name={icon}
        style={{
          color: iconColor!,
        }}
      />
      {numberToShortString(value)}
    </span>
  );
};

export const BlogStatsContainer: FC<IBlogStatsProps> = ({
  reviews = 0,
  comments = 0,
  classContainer,
  countColor,
  iconColor,
}) => {
  if (!reviews && !comments) return null;

  return (
    <div className={classNames(style.stats, classContainer)}>
      <BlogCounter
        countColor={countColor}
        iconColor={iconColor}
        value={reviews!}
      />
      <BlogCounter
        countColor={countColor}
        icon="comments"
        iconColor={iconColor}
        value={comments!}
      />
    </div>
  );
};

interface IFigureProps {
  image: ImageField;
  className?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  objectPosition?: string;
  defaultAlt?: string;
  showPlaceholder?: boolean;
  noImageContent?: ReactNode;
}

interface IBlogPublishDateProps {
  publishAt?: Date | null;
  className?: string;
}

interface IBlogCounterProps {
  value?: number;
  icon?: string;
  className?: string;
  countColor?: ColorField;
  iconColor?: ColorField;
}

interface IBlogStatsProps {
  reviews?: number;
  comments?: number;
  classContainer?: string;
  countColor?: ColorField;
  iconColor?: ColorField;
  style?: CSSProperties;
}
